// src/features/payment/EntityPaymentPanel.tsx
// "Payment" card for the right rail / detail area of another module's entity
// (booking, order, ...). Mirrors the calendar module's EntitySchedulePanel
// precedent (frontend/src/features/calendar/EntitySchedulePanel.tsx): a
// person charging or refunding a booking is looking at the booking, so the
// payment-aspect orchestration lives here instead of under /payment.
//
// Covers the four entity-embedded RPCs the payment IA (docs/ux/payment-ia.md
// §5) placed outside the payment module: ChargeEntity, RefundEntity,
// GetEntityPayment (this panel's own load) and BatchGetEntityPayments (used
// by the host list view to show a payment-status badge per row without an
// N+1 GetEntityPayment per item — see MyBookingsPage.tsx).
//
// Mounted today on the booking module's `/booking/my-bookings` expanded row
// detail (bookings are paid). Any other host entity with a payment aspect —
// an order, a staffing invoice, a property maintenance charge — can reuse
// this panel the same way: pass its Entity in, nothing else to wire.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert, Input } from '@react/components';
import { ChargeEntity } from '@core/api/endpoints/payment/charge_entity';
import { RefundEntity } from '@core/api/endpoints/payment/refund_entity';
import { GetEntityPayment } from '@core/api/endpoints/payment/get_entity_payment';
import { RefundModal } from './RefundModal';
import { formatMoney, errMessage } from './format';
import { mapPaymentEntryStatusToVariant } from './mapStatusToVariant';
import type { Payment } from '@core/types/payment/Payment';
import type { Entity } from '@core/types/entity';

interface EntityPaymentPanelProps {
  /** The host entity carrying the payment aspect (booking, order, ...). */
  entity: Entity;
}

const REFUNDABLE = new Set(['PAYMENT_STATUS_CAPTURED', 'PAYMENT_STATUS_SETTLED', 'PAYMENT_STATUS_PARTIALLY_REFUNDED']);
const CHARGEABLE_AGAIN = new Set(['PAYMENT_STATUS_FAILED', 'PAYMENT_STATUS_CANCELLED']);

export function EntityPaymentPanel({ entity }: EntityPaymentPanelProps) {
  const { t } = useI18n();

  const [payment, setPayment] = useState<Payment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [chargeOpen, setChargeOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [refundOpen, setRefundOpen] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await GetEntityPayment(entity.type_name, entity.id);
      setPayment(res.payment ?? null);
    } catch {
      // No payment aspect yet — not an error state, just "not charged".
      setPayment(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [entity.type_name, entity.id]);

  const submitCharge = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await ChargeEntity(entity.type_name, entity.id, {
        target: entity,
        amount_cents: Math.round(Number(amount || '0') * 100),
        currency,
        payment_method_id: paymentMethodId.trim() || undefined,
        idempotency_key: crypto.randomUUID(),
      });
      setPayment(res.payment ?? null);
      setChargeOpen(false);
      setAmount('');
      setPaymentMethodId('');
    } catch (e) {
      setError(errMessage(e, t('payment.entityPanel.chargeError')));
    } finally {
      setBusy(false);
    }
  };

  const submitRefund = async (value: { full: boolean; amountCents: string; reason: string }) => {
    const res = await RefundEntity(entity.type_name, entity.id, {
      target: entity,
      refund_amount_cents: value.full ? undefined : Math.round(Number(value.amountCents) * 100),
      reason: value.reason,
      idempotency_key: crypto.randomUUID(),
    });
    setPayment(res.payment ?? null);
    setRefundOpen(false);
  };

  if (isLoading) {
    return <div className="h-16 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  }

  return (
    <div className="space-y-2">
      {error && <Alert variant="error">{error}</Alert>}

      {payment ? (
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant={mapPaymentEntryStatusToVariant(payment.status)} size="sm">
              {(payment.status || '').replace('PAYMENT_STATUS_', '')}
            </Badge>
            <span className="font-medium text-foreground">{formatMoney(payment.amount)}</span>
          </div>
          {payment.refunded_amount && payment.refunded_amount.amount > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('payment.entityPanel.refunded')}: {formatMoney(payment.refunded_amount)}
            </p>
          )}
          {payment.failure_message && (
            <p className="text-xs text-destructive">{payment.failure_message}</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('payment.entityPanel.notCharged')}</p>
      )}

      {!chargeOpen && !refundOpen && (
        <div className="flex flex-wrap gap-1.5">
          {(!payment || CHARGEABLE_AGAIN.has(payment.status || '')) && (
            <Button size="sm" variant="secondary" onClick={() => setChargeOpen(true)}>
              {t('payment.entityPanel.charge')}
            </Button>
          )}
          {payment && REFUNDABLE.has(payment.status || '') && (
            <Button size="sm" variant="secondary" onClick={() => setRefundOpen(true)}>
              {t('payment.entityPanel.refund')}
            </Button>
          )}
        </div>
      )}

      {chargeOpen && (
        <div className="space-y-2 rounded-md border border-border p-2">
          <div className="grid grid-cols-2 gap-2">
            <Input label={t('payment.ledger.amount')} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Input label={t('payment.ledger.currency')} value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="USD" />
          </div>
          <Input
            label={t('payment.ledger.paymentMethodId')}
            value={paymentMethodId}
            onChange={(e) => setPaymentMethodId(e.target.value)}
            placeholder={t('payment.entityPanel.paymentMethodOptional')}
          />
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" onClick={submitCharge} isLoading={busy} disabled={!amount}>
              {t('payment.entityPanel.charge')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setChargeOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      <RefundModal
        open={refundOpen}
        maxAmount={payment?.amount}
        onCancel={() => setRefundOpen(false)}
        onSubmit={submitRefund}
      />
    </div>
  );
}

export default EntityPaymentPanel;
